import { useMemo, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { Button, Input, Label } from "reactstrap";
import id from "date-fns/locale/id";
import "react-datepicker/dist/react-datepicker.css";
import {
	QUARTER_PRESETS,
	buildYearOptions,
	formatDisplayRange,
	getQuarterRange,
} from "./dateFilterUtils";

registerLocale("id", id);

const SurveyDateFilter = ({
	preset = "all",
	year,
	startDate = null,
	endDate = null,
	onChange,
}) => {
	const [draftRange, setDraftRange] = useState([startDate, endDate]);
	const yearOptions = useMemo(() => buildYearOptions(3), []);

	const selectedYear = year || new Date().getFullYear();

	const emitChange = (next) => {
		onChange?.({
			preset: next.preset ?? preset,
			year: next.year ?? selectedYear,
			startDate: next.startDate ?? null,
			endDate: next.endDate ?? null,
		});
	};

	const handlePresetClick = (presetId) => {
		if (presetId === "all") {
			setDraftRange([null, null]);
			emitChange({
				preset: "all",
				startDate: null,
				endDate: null,
			});
			return;
		}

		if (presetId === "custom") {
			setDraftRange([startDate, endDate]);
			emitChange({
				preset: "custom",
				startDate,
				endDate,
			});
			return;
		}

		const quarterPreset = QUARTER_PRESETS.find((item) => item.id === presetId);
		if (!quarterPreset?.quarter) return;

		const range = getQuarterRange(selectedYear, quarterPreset.quarter);
		setDraftRange([range.startDate, range.endDate]);
		emitChange({
			preset: presetId,
			startDate: range.startDate,
			endDate: range.endDate,
		});
	};

	const handleYearChange = (event) => {
		const nextYear = Number(event.target.value);
		if (preset.startsWith("q")) {
			const quarter = Number(preset.replace("q", ""));
			const range = getQuarterRange(nextYear, quarter);
			setDraftRange([range.startDate, range.endDate]);
			emitChange({
				preset,
				year: nextYear,
				startDate: range.startDate,
				endDate: range.endDate,
			});
			return;
		}

		emitChange({ year: nextYear, startDate, endDate });
	};

	const handleRangeChange = (dates) => {
		const [start, end] = dates;
		setDraftRange([start, end]);

		if (start && end) {
			emitChange({
				preset: "custom",
				startDate: start,
				endDate: end,
			});
		}
	};

	const handleClear = () => {
		setDraftRange([null, null]);
		emitChange({
			preset: "all",
			startDate: null,
			endDate: null,
		});
	};

	return (
		<div
			className="rounded-4 border p-3 mb-4"
			style={{
				background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)",
				borderColor: "#dbeafe",
			}}
		>
			<div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3 mb-3">
				<div>
					<div className="fw-semibold text-dark mb-1">Filter Periode</div>
					<small className="text-muted">
						Filter KPI, charts, dan daftar responden berdasarkan tanggal terakhir
						survey. Pilih kuartal atau rentang custom. Saat ini:{" "}
						<span className="fw-semibold text-dark">
							{formatDisplayRange(startDate, endDate)}
						</span>
					</small>
				</div>

				<div className="d-flex align-items-center gap-2">
					<Label for="survey-filter-year" className="mb-0 small text-muted">
						Tahun
					</Label>
					<Input
						id="survey-filter-year"
						type="select"
						bsSize="sm"
						value={selectedYear}
						onChange={handleYearChange}
						style={{ width: 100 }}
					>
						{yearOptions.map((optionYear) => (
							<option key={optionYear} value={optionYear}>
								{optionYear}
							</option>
						))}
					</Input>
					{(startDate || endDate) && (
						<Button color="link" size="sm" className="text-decoration-none" onClick={handleClear}>
							Reset
						</Button>
					)}
				</div>
			</div>

			<div className="d-flex flex-wrap gap-2 mb-3">
				{QUARTER_PRESETS.map((item) => {
					const isActive = preset === item.id;
					return (
						<button
							key={item.id}
							type="button"
							onClick={() => handlePresetClick(item.id)}
							className="btn btn-sm rounded-pill px-3"
							style={{
								border: `1px solid ${isActive ? "#3B82F6" : "#cbd5e1"}`,
								background: isActive ? "#3B82F6" : "#ffffff",
								color: isActive ? "#ffffff" : "#334155",
								fontWeight: isActive ? 600 : 500,
							}}
						>
							{item.label}
						</button>
					);
				})}
			</div>

			{preset === "custom" && (
				<div className="row g-3 align-items-end">
					<div className="col-md-6 col-lg-4">
						<Label className="small text-muted mb-1">Rentang tanggal</Label>
						<DatePicker
							selectsRange
							startDate={draftRange[0]}
							endDate={draftRange[1]}
							onChange={handleRangeChange}
							locale="id"
							dateFormat="dd MMM yyyy"
							className="form-control"
							placeholderText="Pilih tanggal mulai – akhir"
							isClearable
							maxDate={new Date()}
							monthsShown={1}
						/>
					</div>
					<div className="col-12">
						<small className="text-muted">
							Pilih tanggal mulai dan tanggal akhir untuk menerapkan filter custom.
						</small>
					</div>
				</div>
			)}
		</div>
	);
};

export default SurveyDateFilter;
